import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Mail, Lock, BarChart3, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import { apiFetch } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch(API_ROUTES.auth.login, {
        method: 'POST',
        body: { email, password },
        auth: false,
      });
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await res.json()
        : { detail: await res.text() };
      if (!res.ok) {
        setError(data.detail || 'Login failed. Please try again.');
        return;
      }
      login(data.access_token, data.user, data.csrf_token);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError(e?.message === 'Failed to fetch'
        ? 'Unable to reach the login service. Please check your connection and try again.'
        : 'Login response could not be processed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient mesh gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/5 blur-[150px]" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo */}
        <Link to="/" className="flex flex-col items-center gap-3 mb-8 group">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
            <BarChart3 size={26} className="text-white" />
          </div>
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">TrackMyFxEA</h1>
            <p className="text-gray-500 text-xs mt-1">Professional EA Performance Analytics</p>
          </div>
        </Link>

        {/* Card */}
        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
          <div className="rounded-3xl p-8 backdrop-blur-xl" style={{
            background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div className="mb-6">
              <h2 className="font-display text-2xl font-bold text-white mb-1">Welcome back</h2>
              <p className="text-sm text-gray-500">Sign in to access your dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Email</label>
                <div className="relative group">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Password</label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Forgot password */}
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                  Forgot password?
                </Link>
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-xl px-4 py-3 text-red-400 text-sm animate-fade-in" style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 group"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #14b8a6, #06b6d4)',
                  color: '#070912',
                  boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#070912] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In
                    <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Google Sign-In */}
            <div className="my-5 relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/8" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-[var(--bg-card)] text-gray-500">or</span>
              </div>
            </div>
            <GoogleSignInButton text="signin_with" />

            {/* Divider + Register */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/8" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-gray-600" style={{ background: 'rgb(11,15,32)' }}>OR</span>
              </div>
            </div>

            <p className="text-center text-gray-500 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
                Create one →
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
